import type { AbstractEngine } from '@babylonjs/core/Engines/abstractEngine'
import { AudioManager } from '../audio/AudioManager'
import { type RecordedRun } from '../contracts/game'
import { BabylonApp } from '../engine/BabylonApp'
import type { VisualDiagnostic } from '../engine/BabylonApp'
import { QualityManager, type QualityLevel } from '../engine/QualityManager'
import { InputManager } from '../input/InputManager'
import { calculateUnlocks } from '../persistence/Progression'
import { ProfileRepository, type LocalProfile } from '../persistence/ProfileRepository'
import { ReplayRepository } from '../persistence/ReplayRepository'
import { FixedStepRunner } from '../simulation/FixedStepRunner'
import { createDefaultTuning } from '../simulation/GameplayTuning'
import { ReplayRecorder } from '../simulation/Replay'
import { Simulation } from '../simulation/Simulation'
import { LocalAnalytics } from '../telemetry/Analytics'
import { PerformanceMonitor } from '../telemetry/PerformanceMonitor'
import { UIController } from '../ui/UIController'
import { ProceduralWorld } from '../world/ProceduralWorld'
import { GameStateMachine } from './GameStateMachine'
import { createDefaultRunFactory } from './RunFactory'
import { RivalController } from '../rivals/RivalController'
import { createConfiguredOnlineServices } from '../online/HttpOnlineServices'
import type { OnlineServices } from '../online/OnlineServices'
import { createRunDescriptor, type RunMode } from './RunModes'

const MILESTONES = [500, 1_500, 3_000, 6_000, 10_000] as const

export interface TestBridge {
  snapshot(): { lateralVelocity: number; steeringQ: number }
  visualDiagnostics(): readonly VisualDiagnostic[]
  forceDeath(): void
  readonly lastRestartMs: number
}

interface ReplayPlayback {
  readonly run: RecordedRun
  inputIndex: number
  steeringQ: number
}

export class Game {
  private readonly engine: AbstractEngine
  private readonly world = new ProceduralWorld()
  private readonly tuning = createDefaultTuning()
  private readonly simulation = new Simulation(this.tuning, this.world)
  private readonly runFactory = createDefaultRunFactory(this.tuning)
  private readonly rivals = new RivalController(this.runFactory)
  private readonly stateMachine = new GameStateMachine()
  private readonly profileRepository = new ProfileRepository()
  private readonly replayRepository = new ReplayRepository()
  private readonly analytics = new LocalAnalytics()
  private readonly performanceMonitor = new PerformanceMonitor()
  private readonly audio = new AudioManager()
  private readonly onlineServices: OnlineServices | null = createConfiguredOnlineServices()
  private readonly input: InputManager
  private readonly renderer: BabylonApp
  private readonly ui: UIController
  private readonly runner: FixedStepRunner
  private readonly qualityManager: QualityManager
  private profile: LocalProfile
  private recorder: ReplayRecorder | null = null
  private playback: ReplayPlayback | null = null
  private runStartedAt = 0
  private elapsedSeconds = 0
  private renderOriginZ = 0
  private nextMilestoneIndex = 0
  private disposed = false
  private _lastRestartMs = 0
  private selectedMode: RunMode = 'endless'

  public constructor(engine: AbstractEngine, canvas: HTMLCanvasElement) {
    this.engine = engine
    this.profile = this.profileRepository.load()
    this.input = new InputManager(canvas)
    this.input.setDragSteering(this.profile.dragSteering)
    const initialQuality: QualityLevel = this.profile.quality === 'auto'
      ? (navigator.webdriver ? 'low' : navigator.maxTouchPoints > 0 ? 'medium' : 'high')
      : this.profile.quality
    this.qualityManager = new QualityManager(initialQuality)
    this.renderer = new BabylonApp(engine, this.profile)
    this.renderer.applyQuality(initialQuality)
    this.ui = new UIController(this.profile, {
      play: (mode) => this.startRun(false, mode),
      retry: () => this.startRun(true, this.selectedMode),
      pause: () => this.pause(),
      resume: () => this.resume(),
      menu: () => this.returnToMenu(),
      mute: () => this.toggleMute(),
      profileChanged: (profile) => this.updateProfile(profile),
      loadReplays: () => this.replayRepository.list(),
      playReplay: (run) => this.startReplay(run),
    })
    this.stateMachine.subscribe((state) => this.ui.showState(state))
    this.runner = new FixedStepRunner(() => this.fixedStep())
    this.stateMachine.send('booted')
    this.world.reset(0xdecafbad)
    this.stateMachine.send('loaded')
    this.ui.finishLoading()
    this.analytics.event('game_loaded')
    this.audio.applyProfile(this.profile)
    void this.refreshOnlineStatus()
    window.addEventListener('keydown', this.handleGlobalKeyDown)
    window.addEventListener('resize', this.handleResize)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  public start(): void {
    this.engine.runRenderLoop(this.renderFrame)
  }

  public get testBridge(): TestBridge {
    const snapshot = (): { lateralVelocity: number; steeringQ: number } => ({
      lateralVelocity: this.simulation.state.player.lateralVelocity,
      steeringQ: this.input.steeringQ,
    })
    const forceDeath = (): void => {
      if (this.stateMachine.state === 'PLAYING') {
        this.simulation.kill('obstacle')
        this.finishRun()
      }
    }
    const getLastRestartMs = (): number => this._lastRestartMs
    return {
      snapshot,
      visualDiagnostics: () => this.renderer.visualDiagnostics(),
      forceDeath,
      get lastRestartMs() { return getLastRestartMs() },
    }
  }

  public dispose(): void {
    if (this.disposed) return
    this.disposed = true
    window.removeEventListener('keydown', this.handleGlobalKeyDown)
    window.removeEventListener('resize', this.handleResize)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    this.input.dispose()
    this.audio.dispose()
    this.renderer.dispose()
  }

  private startRun(isRestart: boolean, mode: RunMode): void {
    const expectedState = isRestart ? 'DEAD' : 'MENU'
    if (this.stateMachine.state !== expectedState) return
    const startTime = performance.now()
    this.stateMachine.send(isRestart ? 'retry' : 'play')
    const seedBuffer = new Uint32Array(1)
    crypto.getRandomValues(seedBuffer)
    if (!isRestart) this.selectedMode = mode
    const descriptor = createRunDescriptor(this.selectedMode, this.tuning.version, seedBuffer[0] ?? 1)
    this.simulation.start(descriptor)
    this.playback = null
    if (this.profile.rivalsEnabled) this.rivals.start(descriptor)
    this.recorder = new ReplayRecorder(descriptor)
    this.runner.reset()
    this.input.reset()
    this.renderOriginZ = 0
    this.nextMilestoneIndex = 0
    this.runStartedAt = performance.now()
    this.renderer.snapCamera(this.simulation.state.player, this.renderOriginZ)
    this.stateMachine.send('started')
    this.ui.updateDistance(0)
    this.analytics.event(isRestart ? 'restart' : 'run_started', { seed: descriptor.seed, mode: descriptor.mode })
    void this.audio.unlock()
    if (isRestart) this._lastRestartMs = performance.now() - startTime
  }

  private fixedStep(): void {
    if (this.stateMachine.state !== 'PLAYING') return
    if (this.playback !== null) {
      while (this.playback.inputIndex < this.playback.run.inputs.length) {
        const transition = this.playback.run.inputs[this.playback.inputIndex]!
        if (transition.tick > this.simulation.state.tick) break
        this.playback.steeringQ = transition.steeringQ
        this.playback.inputIndex += 1
      }
      const tick = this.simulation.state.tick
      this.simulation.step({ tick, steeringQ: this.playback.steeringQ })
      if (!this.simulation.state.player.alive || this.simulation.state.tick >= this.playback.run.tickCount) {
        this.completeReplay()
      }
      return
    }
    const input = { tick: this.simulation.state.tick, steeringQ: this.input.steeringQ }
    this.recorder?.record(input)
    this.simulation.step(input)
    if (this.profile.rivalsEnabled) this.rivals.step()
    const player = this.simulation.state.player
    if (!player.alive) {
      this.finishRun()
      return
    }
    const milestone = MILESTONES[this.nextMilestoneIndex]
    if (milestone !== undefined && player.distance >= milestone) {
      this.ui.showMilestone(`${milestone.toLocaleString()} M // COSMETIC UNLOCKED`)
      this.audio.effect('milestone')
      this.analytics.event('milestone_reached', { distance: milestone })
      this.nextMilestoneIndex += 1
    }
  }

  private finishRun(): void {
    if (this.stateMachine.state !== 'PLAYING') return
    this.stateMachine.send('die')
    const player = this.simulation.state.player
    const distance = Math.floor(player.distance)
    const isNewBest = distance > this.profile.bestDistance
    const bestDistance = Math.max(distance, this.profile.bestDistance)
    const unlocks = calculateUnlocks(bestDistance)
    const mastery: Record<string, number> = {
      ...this.profile.mastery,
      runs: (this.profile.mastery.runs ?? 0) + 1,
    }
    if (this.profile.rivalsEnabled && this.rivals.snapshots().every((rival) => rival.distance <= player.distance)) {
      mastery.rivalWins = (mastery.rivalWins ?? 0) + 1
    }
    this.profile = { ...this.profile, bestDistance, mastery, unlocks }
    this.profileRepository.save(this.profile)
    this.ui.applyProfile(this.profile)
    this.ui.updateBest(bestDistance)
    this.ui.showDeath(distance, bestDistance, isNewBest)
    this.renderer.triggerDeath(player, this.renderOriginZ)
    this.audio.effect('death')
    const recorded = this.recorder?.finish(this.simulation.state, this.simulation.createStateHash())
    if (recorded !== undefined) {
      void this.saveReplay(recorded)
      void this.submitRankedRun(recorded)
    }
    const duration = (performance.now() - this.runStartedAt) / 1_000
    this.analytics.event('run_ended', {
      distance,
      duration,
      deathCause: player.deathCause,
      controlMethod: navigator.maxTouchPoints > 0 ? 'touch' : 'keyboard',
      averageFps: 1_000 / this.performanceMonitor.latest.averageFrameMs,
      minimumFps: this.performanceMonitor.latest.minimumFps,
      graphicsQuality: this.qualityManager.level,
    })
    if (isNewBest) this.analytics.event('personal_best', { distance })
    this.stateMachine.send('settled')
  }

  private async saveReplay(run: RecordedRun): Promise<void> {
    await this.replayRepository.save(run)
  }

  private async submitRankedRun(run: RecordedRun): Promise<void> {
    if (!this.profile.onlineEnabled || this.onlineServices === null || !['daily', 'apex'].includes(run.descriptor.mode ?? '')) return
    const result = await this.onlineServices.submitRun(run)
    if (result.ok) this.ui.showToast(`VERIFIED RUN // RANK ${result.value.rank}`)
    else this.ui.showToast(`ONLINE SUBMISSION // ${result.reason.toUpperCase()}`)
  }

  private startReplay(run: RecordedRun): void {
    if (this.stateMachine.state !== 'MENU') return
    if (run.descriptor.tuningVersion !== this.tuning.version || run.descriptor.replayVersion !== 2) {
      this.ui.showToast('REPLAY INCOMPATIBLE // LEGACY PHYSICS')
      return
    }
    this.stateMachine.send('play')
    this.simulation.start(run.descriptor)
    this.playback = { run, inputIndex: 0, steeringQ: 0 }
    this.recorder = null
    this.runner.reset()
    this.input.reset()
    this.renderOriginZ = 0
    this.nextMilestoneIndex = 0
    this.renderer.snapCamera(this.simulation.state.player, this.renderOriginZ)
    this.stateMachine.send('started')
    this.ui.updateDistance(0)
    this.ui.updateRivals([], 0)
    this.ui.showToast('REPLAY THEATRE // LOCAL GHOST')
    this.analytics.event('replay_started', { seed: run.descriptor.seed, ticks: run.tickCount })
    void this.audio.unlock()
  }

  private completeReplay(): void {
    if (this.playback === null || this.stateMachine.state !== 'PLAYING') return
    this.playback = null
    this.stateMachine.send('menu')
    this.input.reset()
    this.runner.reset()
    this.world.reset(0xdecafbad)
    this.ui.updateBest(this.profile.bestDistance)
    this.ui.showToast('REPLAY COMPLETE // ARCHIVE READY')
    this.analytics.event('replay_completed')
  }

  private pause(): void {
    if (this.stateMachine.state !== 'PLAYING') return
    this.stateMachine.send('pause')
    this.runner.reset()
    this.input.reset()
    this.audio.suspend()
    this.analytics.event('pause')
  }

  private resume(): void {
    if (this.stateMachine.state !== 'PAUSED') return
    this.stateMachine.send('resume')
    this.runner.reset()
    void this.audio.unlock()
  }

  private returnToMenu(): void {
    if (!['PLAYING', 'PAUSED', 'DEAD', 'STARTING'].includes(this.stateMachine.state)) return
    this.stateMachine.send('menu')
    this.playback = null
    this.recorder = null
    this.input.reset()
    this.world.reset(0xdecafbad)
    this.ui.updateBest(this.profile.bestDistance)
  }

  private toggleMute(): void {
    this.updateProfile({ ...this.profile, muted: !this.profile.muted })
  }

  private updateProfile(profile: LocalProfile): void {
    const theme = profile.unlocks.includes(profile.theme) ? profile.theme : 'ion'
    const onlineEnabled = profile.onlineEnabled && this.onlineServices !== null
    this.profile = { ...profile, theme, onlineEnabled }
    this.profileRepository.save(this.profile)
    this.input.setDragSteering(this.profile.dragSteering)
    this.audio.applyProfile(this.profile)
    this.renderer.applyProfile(this.profile)
    this.ui.applyProfile(this.profile)
    if (profile.onlineEnabled && this.onlineServices === null) this.ui.showToast('NETWORK // API NOT CONFIGURED')
    void this.refreshOnlineStatus()
    if (this.profile.quality !== 'auto') this.renderer.applyQuality(this.profile.quality)
    this.analytics.event('control_mode_changed', { dragSteering: this.profile.dragSteering })
  }

  private async refreshOnlineStatus(): Promise<void> {
    if (this.onlineServices === null) {
      this.ui.updateOnlineStatus('local')
      return
    }
    if (!this.profile.onlineEnabled) {
      this.ui.updateOnlineStatus('disabled')
      return
    }
    this.ui.updateOnlineStatus('connecting')
    const status = await this.onlineServices.getStatus()
    if (this.profile.onlineEnabled) this.ui.updateOnlineStatus(status.available ? 'online' : 'unavailable')
  }

  private readonly renderFrame = (): void => {
    if (this.disposed) return
    const frameMs = Math.min(100, this.engine.getDeltaTime())
    const deltaSeconds = frameMs / 1_000
    this.elapsedSeconds += deltaSeconds
    if (this.stateMachine.state === 'PLAYING') this.runner.advance(deltaSeconds)
    const report = this.performanceMonitor.addFrame(frameMs)
    if (report !== null && this.profile.quality === 'auto') {
      const change = this.qualityManager.sample({
        averageFrameMs: report.averageFrameMs,
        p95FrameMs: report.p95FrameMs,
        nowSeconds: this.elapsedSeconds,
      })
      if (change !== null) {
        this.renderer.applyQuality(change.to)
        this.analytics.event('graphics_auto_changed', { ...change })
      }
    }

    const player = this.simulation.state.player
    if (this.stateMachine.state === 'MENU' || this.stateMachine.state === 'LOADING') {
      this.renderer.renderMenu(this.world.activeChunks, this.elapsedSeconds, deltaSeconds)
      return
    }
    if (player.position.z - this.renderOriginZ > 2_000) {
      this.renderOriginZ = Math.floor(player.position.z / 2_000) * 2_000
    }
    const speedNormalized = (player.forwardVelocity - this.tuning.initialSpeed)
      / (this.tuning.maximumSpeed - this.tuning.initialSpeed)
    this.audio.setSpeed(speedNormalized)
    this.ui.updateDistance(player.distance)
    const rivalSnapshots = this.profile.rivalsEnabled && this.playback === null ? this.rivals.snapshots() : []
    this.ui.updateRivals(rivalSnapshots, player.distance)
    this.renderer.renderGame(
      player,
      this.world.activeChunks,
      this.simulation.state.tick,
      this.runner.alpha,
      this.renderOriginZ,
      this.elapsedSeconds,
      deltaSeconds,
      speedNormalized,
      rivalSnapshots,
    )
  }

  private readonly handleGlobalKeyDown = (event: KeyboardEvent): void => {
    if (event.code === 'F3') {
      event.preventDefault()
      const enabled = this.renderer.toggleColliderDebug()
      this.ui.showToast(`COLLIDER WIREFRAMES // ${enabled ? 'ON' : 'OFF'}`)
      return
    }
    if (event.target instanceof HTMLInputElement
      || event.target instanceof HTMLSelectElement
      || event.target instanceof HTMLButtonElement
      || document.querySelector('dialog[open]') !== null) return
    const activation = ['Space', 'Enter'].includes(event.code)
      || (this.stateMachine.state === 'DEAD' && ['KeyA', 'KeyD', 'ArrowLeft', 'ArrowRight'].includes(event.code))
    if (!activation) return
    event.preventDefault()
    if (this.stateMachine.state === 'MENU') this.startRun(false, this.selectedMode)
    else if (this.stateMachine.state === 'DEAD') this.startRun(true, this.selectedMode)
    else if (this.stateMachine.state === 'PAUSED') this.resume()
  }

  private readonly handleResize = (): void => {
    this.renderer.resize()
    const isPortraitTouch = navigator.maxTouchPoints > 0 && window.innerHeight > window.innerWidth
    if (isPortraitTouch && this.stateMachine.state === 'PLAYING') this.pause()
  }

  private readonly handleVisibilityChange = (): void => {
    this.input.reset()
    this.runner.reset()
    if (document.hidden) {
      if (this.stateMachine.state === 'PLAYING') this.pause()
      this.audio.suspend()
    }
  }
}
